import {
  hasMarketingDirectEntryQueryParams,
  useHistory,
  useQueryParams,
  useQueryParamsCallback,
} from '@aboutyou/router';
import {useEffect} from 'react';

/**
 * Defines on load time if an ADP was send from the server or not.
 * States are
 * - true: quick view was send from the server
 * - false: quick view was not send from the server
 * - null: no quick view information found (e.g. on non article pages)
 */
// eslint-disable-next-line spaced-comment
let serverSendQuickViewState = /*@__PURE__*/ (() => {
  if (!process.browser) {
    return null;
  }
  try {
    const elem = document.querySelector<HTMLSpanElement>(
      '[data-quick-view-enabled]',
    );
    if (!elem) {
      return null;
    }
    const value = elem.dataset.quickViewEnabled;
    if (!value) {
      return null;
    }

    return value === 'true';
  } catch (e) {
    return null;
  }
})();

const resetServerSendQuickViewState = () => {
  serverSendQuickViewState = null;
};

/**
 * Returns the information whether quick view should be rendered or not.
 * Normally this is done using query parameters from the url. But in case the page is loaded from the server, we should respect this information and render according to what was send from the server.
 * The server side case is only important because we have caching in place which does not respect the query parameters and just sends out what came first.
 */
export function useIsQuickView() {
  const isQuickView = useBrowserIsQuickView();
  const history = useHistory();
  const getParams = useQueryParamsCallback();

  // correct the quick view url after mount
  useEffect(() => {
    const serverDiffersFromClient = isQuickView !== serverSendQuickViewState;
    if (serverSendQuickViewState !== null && serverDiffersFromClient) {
      // params don't include utm parameter by default
      const newSearch = new URLSearchParams(getParams());
      if (serverSendQuickViewState) {
        newSearch.set('quick', 'view');
      } else {
        newSearch.delete('quick');
      }
      history.replace({
        ...history.location,
        search: newSearch.toString(),
      });
    }
    resetServerSendQuickViewState();
  }, []);

  if (serverSendQuickViewState !== null) {
    return serverSendQuickViewState;
  }

  return isQuickView;
}

const useBrowserIsQuickView = () => {
  const params = useQueryParams({includeUtmParams: true});

  /**
   * ASFE-3850
   *
   * Provides a parameter that can circumvent the quick-view activation by utm_ parameters
   */
  const viewParam = params.get('view');
  if (viewParam === 'adp') {
    return false;
  }

  const quickParam = params.get('quick');

  if (quickParam === 'view') {
    return true;
  }

  return hasMarketingDirectEntryQueryParams(params);
};
